import { h } from './ui.ts';

/** The design system's components (styles.css): buttons, badges, page headers, dialogs, toasts, skeletons. */

type Child = Node | string | null | undefined;
export type Variant = 'default' | 'secondary' | 'outline' | 'ghost' | 'destructive';

export function button(
  children: Child | Child[],
  onClick: (e: Event) => void,
  opts: { variant?: Variant; size?: 'sm' | 'lg' | 'icon'; label?: string; type?: string; extra?: string } = {},
): HTMLButtonElement {
  const cls = ['btn', opts.variant && opts.variant !== 'default' ? opts.variant : '', opts.size ?? '', opts.extra ?? '']
    .filter(Boolean)
    .join(' ');
  const attrs: Record<string, string | ((e: Event) => void)> = { class: cls, type: opts.type ?? 'button' };
  if (opts.label) attrs['aria-label'] = opts.label;
  attrs.onclick = onClick;
  return h('button', attrs, ...(Array.isArray(children) ? children : [children]));
}

export function linkButton(children: Child[], href: string, variant: Variant = 'outline', size = ''): HTMLElement {
  const external = /^https?:/.test(href);
  return h(
    'a',
    {
      class: `btn ${variant === 'default' ? '' : variant} ${size}`.trim(),
      href,
      ...(external ? { target: '_blank', rel: 'noopener' } : {}),
    },
    ...children,
  );
}

export const badge = (tone: string, ...children: Child[]) => h('span', { class: `badge ${tone}` }, ...children);

export function pageHeader(title: Child, sub: Child | Child[], ...actions: Array<HTMLElement | null>): HTMLElement {
  return h(
    'header',
    { class: 'page-head' },
    h(
      'div',
      { class: 'grow' },
      h('h1', {}, title),
      h('p', { class: 'page-sub' }, ...(Array.isArray(sub) ? sub : [sub])),
    ),
    h('div', { class: 'row' }, ...actions),
  );
}

export const section = (title: string, ...children: Child[]) =>
  h('section', { class: 'section' }, h('h2', { class: 'section-title' }, title), ...children);

export const emptyState = (title: string, body: string, action?: HTMLElement) =>
  h('div', { class: 'empty' }, h('b', {}, title), h('span', {}, body), action ?? null);

export const skeletonRows = (n = 3) =>
  h(
    'div',
    { class: 'stack', 'aria-busy': 'true', 'aria-label': 'Loading' },
    ...Array.from({ length: n }, () => h('div', { class: 'skeleton', style: 'height:64px' })),
  );

/** A toast in the corner for a few seconds; errors stay a little longer. */
export function toast(message: string, tone: 'default' | 'danger' = 'default') {
  let host = document.querySelector<HTMLElement>('.toasts');
  if (!host) {
    host = h('div', { class: 'toasts', role: 'status', 'aria-live': 'polite' });
    document.body.append(host);
  }
  const el = h('div', { class: `toast ${tone}` }, message);
  host.append(el);
  setTimeout(() => el.remove(), tone === 'danger' ? 6000 : 3500);
}

export const errorText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** Runs an action with the button disabled; failures become a toast. */
export async function busy(btn: HTMLButtonElement, fn: () => Promise<void>) {
  btn.disabled = true;
  try {
    await fn();
  } catch (err) {
    toast(errorText(err), 'danger');
  } finally {
    btn.disabled = false;
  }
}

/** A modal dialog around a form; `submit` returns true to close it. Escape and Cancel close it too. */
export function openDialog(
  title: string,
  body: Child[],
  submit: { label: string; variant?: Variant; run: () => Promise<boolean> },
): HTMLDialogElement {
  const dlg = h('dialog', { class: 'dialog', 'aria-label': title });
  const ok = button(submit.label, () => {}, { variant: submit.variant ?? 'default', type: 'submit' });
  const form = h(
    'form',
    { method: 'dialog' },
    h('h2', {}, title),
    ...body,
    h(
      'div',
      { class: 'dialog-actions' },
      button('Cancel', () => dlg.close(), { variant: 'ghost' }),
      ok,
    ),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void busy(ok, async () => {
      if (await submit.run()) dlg.close();
    });
  });
  dlg.append(form);
  dlg.addEventListener('close', () => dlg.remove());
  document.body.append(dlg);
  dlg.showModal();
  return dlg;
}

export function confirmDialog(title: string, text: string, label: string, run: () => Promise<void>) {
  openDialog(title, [h('p', {}, text)], {
    label,
    variant: 'destructive',
    run: async () => {
      await run();
      return true;
    },
  });
}

export function field(label: string, control: HTMLElement, hint?: string): HTMLElement {
  return h('label', { class: 'field' }, label, control, hint ? h('small', {}, hint) : null);
}
